import { List } from "@mui/material";
import {
  SMART_CHECKOUT_ID,
  SMART_CONTENT_ID,
  SMART_MERCHANDISING_ID,
  SMART_SHIPPING_ID,
} from "@src/constants/billing";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useCanViewSitewideAnalytics } from "@src/components/LimitAccess/check-analytics-access";
import { IgListItemLink } from "@src/components/Navigation/BaseSideNav/IgListItemLink";
import { SectionHeader } from "@src/components/Navigation/BaseSideNav/SectionHeader";
import { SwitchToMobile } from "@src/components/Navigation/SwitchToMobile/SwitchToMobile";
import { getModuleTrialNavBadge } from "@src/components/Navigation/BaseSideNav/module-trial-nav-badge";
import { NavBadge } from "@src/components/Navigation/BaseSideNav/NavBadge";
import { usePostPurchaseDraftBadge } from "@src/components/Navigation/BaseSideNav/usePostPurchaseDraftBadge";
import { useModuleTrials } from "@src/features/sharedExperience/hooks/use-module-trials";
import { StandardRoutes } from "@src/components/Utils/routes";
import { useGetStartedEnabled } from "@src/features/getStarted/hooks/useGetStartedEnabled";
import { useDialogs } from "@src/hooks/dialog";
import { EXPERIENCE_ROUTES, GENERAL_ROUTES } from "@src/routes/routes";

interface TopNavSectionProps {
  drawerOpen: boolean;
}

export const TopNavSection = ({ drawerOpen }: TopNavSectionProps) => {
  const organization = useGetOrg();
  const { openDialog } = useDialogs();
  const hasSitewideAnalyticsAccess = useCanViewSitewideAnalytics();
  const getStartedEnabled = useGetStartedEnabled();
  const moduleTrials = useModuleTrials();
  const trialBadgeFor = (moduleId: string) => {
    const trial = moduleTrials.find(
      (candidate) => candidate.moduleId === moduleId
    );
    const badge = trial && getModuleTrialNavBadge(trial.endDate);
    return badge && <NavBadge {...badge} />;
  };
  const showPostPurchaseDraftBadge = usePostPurchaseDraftBadge(
    organization.data?.org.id
  );

  if (!organization.data) {
    return null;
  }
  const orgId = organization.data.org.id;

  return (
    <List sx={{ px: drawerOpen ? 2 : 4 }}>
      <SwitchToMobile drawerOpen={drawerOpen} />
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().quickFind}
        hasAccess={true}
        handleClick={() => {
          openDialog("cmdk");
        }}
        isLink={false}
      />
      {getStartedEnabled && (
        <IgListItemLink
          drawerOpen={drawerOpen}
          Route={StandardRoutes().getStarted}
          hasAccess={true}
          exact={true}
        />
      )}
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().home}
        hasAccess={true}
        exact={true}
      />
      <IgListItemLink
        Route={StandardRoutes({ orgId }).sitewideAnalytics}
        drawerOpen={drawerOpen}
        hasAccess={hasSitewideAnalyticsAccess}
        exact={true}
        selectedAcceptedRoute={`${GENERAL_ROUTES.SitewideAnalytics}/`}
      />
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().getInspired}
        hasAccess={true}
        exact={true}
      />
      <SectionHeader title="Smart experiences" drawerOpen={drawerOpen} />
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().content}
        hasAccess={true}
        exact={true}
        selectedAcceptedRoute={`${EXPERIENCE_ROUTES.Experiences_Content}/`}
        chip={trialBadgeFor(SMART_CONTENT_ID)}
      />
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().merchandising}
        hasAccess={true}
        exact={true}
        selectedAcceptedRoute={`${EXPERIENCE_ROUTES.Experiences_Merchandising}/`}
        chip={trialBadgeFor(SMART_MERCHANDISING_ID)}
      />
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().pricing}
        hasAccess={true}
        exact={true}
        selectedAcceptedRoute={`${EXPERIENCE_ROUTES.Experiences_Pricing}/`}
      />
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().shipping}
        hasAccess={true}
        exact={true}
        selectedAcceptedRoute={`${EXPERIENCE_ROUTES.Experiences_Shipping}/`}
        chip={trialBadgeFor(SMART_SHIPPING_ID)}
      />
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().checkout}
        hasAccess={true}
        exact={true}
        selectedAcceptedRoute={`${EXPERIENCE_ROUTES.Experiences_Checkout}/`}
        chip={trialBadgeFor(SMART_CHECKOUT_ID)}
      />
      <IgListItemLink
        drawerOpen={drawerOpen}
        Route={StandardRoutes().postPurchase}
        hasAccess={true}
        exact={true}
        selectedAcceptedRoute={`${EXPERIENCE_ROUTES.Experiences_PostPurchaseModule}/`}
        chip={
          showPostPurchaseDraftBadge ? (
            <NavBadge label="Draft ready" />
          ) : (
            trialBadgeFor(SMART_CHECKOUT_ID)
          )
        }
      />
    </List>
  );
};
